/**
 * 文档管理组件 - DocumentManage
 *
 * 功能特性：
 * 1. 文档列表展示 - 支持列表视图和网格视图
 * 2. 批量操作 - 批量删除、批量下载
 * 3. 高级搜索 - 多条件筛选和搜索
 * 4. 拖拽上传 - 支持拖拽文件到页面直接上传
 * 5. 文件预览 - 支持多种文件格式的在线预览
 * 6. 权限控制 - 基于用户权限的操作控制
 * 7. 审核流程 - 文档审核状态管理
 * 8. 统计信息 - 文档数量、大小等统计
 *
 * 支持的文件格式：
 * - 文档：PDF, DOC, DOCX, TXT
 * - 表格：XLS, XLSX
 * - 图片：PNG, JPG, JPEG, GIF, BMP, WEBP
 *
 * 使用说明：
 * 1. 上传文档：点击"上传文档"按钮或拖拽文件到页面
 * 2. 查看文档：点击预览、详情或下载按钮
 * 3. 批量操作：勾选文档后使用批量操作按钮
 * 4. 高级搜索：点击"高级搜索"按钮进行多条件筛选
 * 5. 审核文档：有审核权限的用户可以审核待审核的文档
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Table, Button, message, Dropdown, Menu, Space, Tag, Card, Modal, Select, Upload, Form, Input, Checkbox,
  Row, Col, Typography, Divider, Tooltip, Badge, Progress, Alert, Empty, Spin, InputNumber, DatePicker,
  Tabs, List, Avatar, Statistic, Drawer, Switch, Radio, Slider, Pagination, TreeSelect, Tree
} from 'antd';
import UploadTaskList from './UploadTaskList';
import {
  EyeOutlined, ReloadOutlined, UploadOutlined, CheckCircleOutlined, CloseCircleOutlined,
  FundViewOutlined, EllipsisOutlined, SearchOutlined, FilterOutlined, DownloadOutlined,
  DeleteOutlined, EditOutlined, CopyOutlined, ShareAltOutlined, StarOutlined, StarFilled,
  FolderOutlined, FolderOpenOutlined, FileOutlined, PictureOutlined, FileTextOutlined, FilePdfOutlined,
  FileExcelOutlined, FileWordOutlined, InboxOutlined, CloudUploadOutlined, SettingOutlined,
  SortAscendingOutlined, SortDescendingOutlined, FullscreenOutlined, FullscreenExitOutlined,
  ClockCircleOutlined, UserOutlined, PlusOutlined
} from '@ant-design/icons';
// 保留 docx-preview 仅在预览组件中使用，避免管理页加载体积
// import { renderAsync } from 'docx-preview';
import * as XLSX from 'xlsx';
import axios from '../../api';
import { getMediaUrl } from '../../config';
import permissionManager from '../../utils/permissions';
import { downloadFile, downloadFiles } from '../../utils/download';

import DocumentPreview, { ZoomControls } from './DocumentPreviewOptimized';
import copy from 'copy-to-clipboard';
import theme from '../../theme';
import dayjs from 'dayjs';

const { Title, Text, Paragraph } = Typography;
const { RangePicker } = DatePicker;
const { TabPane } = Tabs;



const FILE_TYPE_ICONS = {
  pdf: <FilePdfOutlined style={{ color: '#ff4d4f' }} />,
  doc: <FileWordOutlined style={{ color: '#1890ff' }} />,
  docx: <FileWordOutlined style={{ color: '#1890ff' }} />,
  xls: <FileExcelOutlined style={{ color: '#52c41a' }} />,
  xlsx: <FileExcelOutlined style={{ color: '#52c41a' }} />,
  txt: <FileTextOutlined style={{ color: '#722ed1' }} />,
  png: <PictureOutlined style={{ color: '#fa8c16' }} />,
  jpg: <PictureOutlined style={{ color: '#fa8c16' }} />,
  jpeg: <PictureOutlined style={{ color: '#fa8c16' }} />,
  gif: <PictureOutlined style={{ color: '#fa8c16' }} />,
  bmp: <PictureOutlined style={{ color: '#fa8c16' }} />,
  webp: <PictureOutlined style={{ color: '#fa8c16' }} />,
  svg: <PictureOutlined style={{ color: '#fa8c16' }} />,
  tiff: <PictureOutlined style={{ color: '#fa8c16' }} />,
  tif: <PictureOutlined style={{ color: '#fa8c16' }} />,
};

// 支持的图片格式
const SUPPORTED_IMAGE_FORMATS = ['png', 'jpg', 'jpeg', 'gif', 'bmp', 'webp', 'svg', 'tiff', 'tif'];

// 支持的视频格式
const SUPPORTED_VIDEO_FORMATS = ['mp4', 'avi', 'mov', 'wmv', 'flv', 'webm'];

// 检查是否为图片文件
const isImageFile = (fileName) => {
  const extension = fileName.split('.').pop().toLowerCase();
  return SUPPORTED_IMAGE_FORMATS.includes(extension);
};

// 检查是否为视频文件
const isVideoFile = (fileName) => {
  const extension = fileName.split('.').pop().toLowerCase();
  return SUPPORTED_VIDEO_FORMATS.includes(extension);
};

const DocumentManage = () => {
  const { t } = useTranslation();
  const [documents, setDocuments] = useState([]);
  const [documentGroups, setDocumentGroups] = useState([]);
  const [users, setUsers] = useState([]); // 添加用户列表状态
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState('list'); // 'list', 'grid'
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [uploadModalVisible, setUploadModalVisible] = useState(false);
  const [previewModalVisible, setPreviewModalVisible] = useState(false);
  const [groupModalVisible, setGroupModalVisible] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [filters, setFilters] = useState({});
  const [advancedSearchVisible, setAdvancedSearchVisible] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [folderTree, setFolderTree] = useState([]);
  const [createFolderModalVisible, setCreateFolderModalVisible] = useState(false);
  const [createFolderForm] = Form.useForm();
  const [selectedFolderId, setSelectedFolderId] = useState(null);
  const [selectedFolderName, setSelectedFolderName] = useState('');
  const [form] = Form.useForm();
  const [groupForm] = Form.useForm();
  const [searchForm] = Form.useForm();
  const [sortConfig, setSortConfig] = useState({ field: 'created_at', order: 'desc' });
  const [fullscreenPreview, setFullscreenPreview] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  
  // 加载当前用户与权限，确保权限控件正确可用
  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get('/api/userinfo/');
        setCurrentUser(res.data);
      } catch (e) {
        console.error('获取当前用户失败:', e);
      }
      try {
        await permissionManager.loadPermissions();
      } catch (e) {
        console.warn('权限加载失败:', e);
      }
    })();
  }, []);

  // 根据角色控制功能显示
  const shouldHideFeature = useCallback((feature) => {
    if (!currentUser) return false;
    
    const userRole = currentUser.role;
    
    // 调试信息
    console.log('角色权限检查:', {
      feature,
      userRole,
      username: currentUser.username,
      shouldHide: false
    });
    
    // 需要屏蔽的功能列表 - 使用英文角色值
    const restrictedFeatures = {
      'owner_filter': ['academic_admin', 'student', 'teacher'], // 所有者过滤框
      'file_type_filter': ['academic_admin', 'student', 'teacher'], // 文件类型过滤框
      'advanced_search': ['academic_admin', 'student', 'teacher'], // 高级搜索
      'file_type_column': ['academic_admin', 'student', 'teacher'], // 文件类型列
    };
    
    const shouldHide = restrictedFeatures[feature]?.includes(userRole) || false;
    
    // 更新调试信息
    console.log('角色权限检查结果:', {
      feature,
      userRole,
      username: currentUser.username,
      shouldHide,
      restrictedRoles: restrictedFeatures[feature]
    });
    
    return shouldHide;
  }, [currentUser]);

  // 文件上传相关
  const [dragOver, setDragOver] = useState(false);
  const uploadRef = useRef(null);

  const [fileTypeOptions, setFileTypeOptions] = useState([]);

  const [totalFileSize, setTotalFileSize] = useState(0);

  // 记录用户是否手动修改过标题
  const [titleTouched, setTitleTouched] = useState(false);

  // 新增状态
  const [filterGroups, setFilterGroups] = useState([]);
  const [filterUsers, setFilterUsers] = useState([]);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [uploadTaskListVisible, setUploadTaskListVisible] = useState(false);

  // 获取支持的文件类型选项
  const fetchFileTypeOptions = useCallback(async () => {
    try {
      const res = await axios.get('/api/documents/supported-file-types/');
      setFileTypeOptions(res.data || []);
    } catch (err) {
      console.error('获取文件类型选项失败:', err);
      // 如果接口失败，使用默认选项
      setFileTypeOptions([
        { ext: ".doc,.docx", label: "Word文档" },
        { ext: ".pdf", label: "PDF文档" },
        { ext: ".txt,.rtf", label: "文本文档" },
        { ext: ".jpg,.jpeg,.png,.gif,.bmp,.svg", label: "图片文件" },
        { ext: ".mp4,.avi,.mov,.wmv,.flv,.webm", label: "视频文件" },
        { ext: ".mp3,.wav,.flac,.aac,.ogg", label: "音频文件" },
        { ext: ".xls,.xlsx", label: "Excel表格" },
        { ext: ".csv", label: "CSV表格" },
        { ext: ".ppt,.pptx", label: "PowerPoint演示" },
        { ext: "*", label: "其他文件" },
      ]);
    }
  }, []);

  const fetchAllGroups = useCallback(async () => {
    try {
      // 使用用户可访问的文档组接口，这个接口对学生用户也开放
      const res = await axios.get('/api/user-accessible-document-groups/');
      // 兼容分页/非分页
      const groups = Array.isArray(res.data) ? res.data : (res.data?.results || []);
      setFilterGroups(groups);
    } catch (err) {
      console.error('获取全部空间失败:', err);
      // 回退为从当前文档页推断
      setFilterGroups(prev => prev.length ? prev : []);
    }
  }, []);

  // 请求竞态保护：仅最后一次 fetch 结果生效
  const fetchSeqRef = useRef(0);
  const fetchDocumentsRef = useRef(null);

  const fetchDocuments = useCallback(async (page = 1, pageSize = 10, customFilters = filters, sortField = 'created_at', sortOrder = 'desc', currentSelectedFolderId = selectedFolderId) => {
    const seq = ++fetchSeqRef.current;
    setLoading(true);
    try {
      const params = { 
        page, 
        page_size: pageSize, 
        ordering: sortOrder === 'desc' ? `-${sortField}` : sortField,
        ...customFilters 
      };
      // 目录过滤规则：优先采用当前选中的文件夹ID，其次采用自定义过滤中的 folder
      const effectiveFolderId = currentSelectedFolderId || customFilters?.folder || null;
      if (effectiveFolderId) {
        // 子目录：仅该文件夹内
        delete params.folder__isnull;
        params.folder = effectiveFolderId;
      } else {
        // 根目录：仅无 folder 的文件
        delete params.folder;
        params['folder__isnull'] = 'true';
      }

      console.log('fetchDocuments 请求参数:', params);
      const res = await axios.get('/api/documents/', { params });
      // 仅处理最后一次请求
      if (seq !== fetchSeqRef.current) return;
      const data = res.data;
      console.log('fetchDocuments 响应数据:', data);
      setDocuments(data.results || []);
      setPagination({
        current: page,
        pageSize,
        total: data.count || 0,
      });
      console.log('设置分页状态:', { current: page, pageSize, total: data.count || 0 });
      setTotalFileSize((data.results || []).reduce((sum, d) => sum + (d.file_size || 0), 0));
      // 用户列表由 fetchUsers 函数单独获取，不在这里处理
    } catch (err) {
      // 仅处理最后一次请求的错误态
      if (seq === fetchSeqRef.current) {
        setDocuments([]);
        setPagination({ current: 1, pageSize: 10, total: 0 });
        setTotalFileSize(0);
      }
    } finally {
      if (seq === fetchSeqRef.current) setLoading(false);
    }
  }, []);

  // 获取文档组列表
  const fetchDocumentGroups = useCallback(async () => {
    try {
      // 使用新的API接口获取用户可访问的文档组
      const res = await axios.get('/api/user-accessible-document-groups/');

      // 兼容不同的返回格式
      let groups = [];
      if (res.data && Array.isArray(res.data)) {
        // 如果返回的是直接数组
        groups = res.data;
      } else if (res.data && res.data.results && Array.isArray(res.data.results)) {
        // 如果返回的是分页格式
        groups = res.data.results;
      }

      console.log('用户可访问的文档组:', groups);
      setDocumentGroups(groups || []);

      // 若当前未选空间，不设置默认空间，显示所有空间的文件
      if ((!filters || !filters.group) && groups && groups.length > 0) {
        // 不设置默认空间，保持 filters 为空，这样会显示所有空间的文件
        // 主动刷新文档（不设置空间过滤）
        fetchDocuments(1, pagination.pageSize || 10, {}, 'created_at', 'desc', selectedFolderId);
        // 不加载特定空间的文件夹树，因为要显示所有空间的文件
      } else if (groups && groups.length > 0 && filters?.group) {
        // 如果已经有选中的空间，确保加载对应的文件夹树
        fetchFolderTree(filters?.group);
      }
    } catch (err) {
      console.error('获取文档组别列表失败:', err);
              message.error(t('common.failedToGetDocumentGroups'));
      setDocumentGroups([]);
    }
  }, [filters, pagination.pageSize]);

  // 获取用户列表
  const fetchUsers = useCallback(async () => {
    try {
      // 使用新的API接口获取文档所有者列表，避免用户权限校验
      const res = await axios.get('/api/document-owners/');
      
      let userList = [];
      
      // 兼容不同的返回格式
      if (res.data && Array.isArray(res.data)) {
        userList = res.data;
      } else if (res.data && res.data.results && Array.isArray(res.data.results)) {
        userList = res.data.results;
      }

      setUsers(userList || []);
      setFilterUsers(userList || []); // 同时设置过滤用的用户列表
      console.log('获取到的文档所有者列表:', userList);
    } catch (err) {
      console.error('获取文档所有者列表失败:', err);
              message.error(t('common.failedToGetDocumentOwners'));
      setUsers([]);
    }
  }, []);

  const fetchFolderTree = useCallback(async (groupId) => {
    try {
      const params = {};
      // 只有当明确选择了特定空间时才传递 group 参数
      // 如果没有选择空间，不传递参数，让后端返回用户有权限访问的所有空间的文件夹
      if (groupId) {
        params.group = groupId;
      }
      const res = await axios.get('/api/folders/tree', { params });
      setFolderTree(res.data || []);
      // 若当前选择的文件夹不在新树中，清空选择
      if (selectedFolderId) {
        const exists = JSON.stringify(res.data || []).includes(`"id":${selectedFolderId}`);
        if (!exists) {
          setSelectedFolderId(null);
          setSelectedFolderName('');
        }
      }
    } catch (e) {
      console.error('获取文件夹树失败:', e);
      setFolderTree([]);
    }
  }, [selectedFolderId]);

  useEffect(() => {
    fetchFileTypeOptions();
    fetchDocumentGroups();
    fetchAllGroups();
    fetchUsers();
    // 首次加载默认拉取所有有权限的文档
    // 注意：这里不直接调用 fetchDocuments，而是等待 fetchDocumentGroups 完成后自动触发
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    fetchFolderTree(filters?.group);
  }, [filters?.group, fetchFolderTree]);

  // 合并的useEffect，避免重复调用
  useEffect(() => {
    // 确保用户信息和权限已加载
    if (currentUser && documentGroups.length > 0) {
      // 使用setTimeout避免立即执行，给其他状态更新留出时间
      const timer = setTimeout(() => {
        fetchDocuments(pagination.current, pagination.pageSize, filters, 'created_at', 'desc', selectedFolderId);
      }, 100);
      
      return () => clearTimeout(timer);
    }
  }, [pagination.current, pagination.pageSize, currentUser, documentGroups.length, selectedFolderId, JSON.stringify(filters)]);

  const handleTableChange = (pagination, filters, sorter) => {
    // 处理排序
    let sortField = 'created_at';
    let sortOrder = 'desc'; // 默认按创建时间降序（从新到老）
    
    if (sorter && sorter.field) {
      sortField = sorter.field;
      sortOrder = sorter.order === 'ascend' ? 'asc' : 'desc';
    }
    
    // 使用当前分页状态，只更新排序
    fetchDocuments(pagination.current, pagination.pageSize, filters, sortField, sortOrder, selectedFolderId);
  };

  const handleTableSort = (sorter) => {
    // 处理排序
    let sortField = 'created_at';
    let sortOrder = 'desc'; // 默认按创建时间降序（从新到老）
    
    if (sorter && sorter.field) {
      sortField = sorter.field;
      sortOrder = sorter.order === 'ascend' ? 'asc' : 'desc';
    }
    
    // 使用当前分页状态，只更新排序
    fetchDocuments(pagination.current, pagination.pageSize, filters, sortField, sortOrder, selectedFolderId);
  };

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    fetchDocuments(1, pagination.pageSize, newFilters, 'created_at', 'desc', selectedFolderId);
  };

  const handlePreview = (record) => {
    setSelectedDocument(record);
    setPreviewModalVisible(true);
  };

  // 统一的下载函数

  const handleViewDetail = useCallback((record) => {
    setSelectedDocument(record);
    setDetailModalVisible(true);
  }, []);



  const handleUploadFinish = async (values) => {
    console.log('=== 开始处理文档上传/更新 ===');
    console.log('表单值:', values);
    console.log('文件字段值:', values.file);
    console.log('文件字段类型:', typeof values.file);
    console.log('文件字段长度:', values.file?.length);
    console.log('文件字段是否为数组:', Array.isArray(values.file));

    setLoading(true);
    
    // 立即隐藏上传弹框
    setUploadModalVisible(false);

    // 处理文件上传
    if (values.file && values.file.length > 0) {
      const fileObj = values.file[0];
      if (fileObj.originFileObj) {
        // 使用前端直传OSS
        await handleDirectOSSUpload(fileObj.originFileObj, values);
      } else if (fileObj.response && fileObj.response.url) {
        // 如果文件已经上传过，使用URL
        await handleFileUrlUpload(fileObj.response.url, values);
      } else {
        console.log('文件对象格式异常:', fileObj);
        message.error('文件格式错误，请重新选择');
        setLoading(false);
        return;
      }
    } else if (selectedDocument) {
      console.log('更新操作：没有新文件上传，将保留原文件');
      await handleFileUrlUpload(null, values);
    } else {
      console.log('新建操作：没有文件上传');
      message.error(t('common.pleaseSelectFileToUpload'));
      setLoading(false);
      return;
    }
  };

  // 前端直传OSS处理
  const handleDirectOSSUpload = async (file, values) => {
    let task = null;
    let lastReportedProgress = -1;
    try {
      console.log('=== 开始前端直传OSS ===');
      console.log('文件信息:', {
        name: file.name,
        size: file.size,
        type: file.type
      });

      // 1. 获取STS临时凭证
      const stsResponse = await axios.post('/api/oss/sts-token/', {
        filename: file.name,
        folder: 'documents'
      });

      if (!stsResponse.data.success) {
        throw new Error(stsResponse.data.error || '获取STS凭证失败');
      }

      const stsData = stsResponse.data.data;
      console.log('STS凭证获取成功:', stsData);

      // 2. 创建上传任务
      const taskResponse = await axios.post('/api/upload-tasks/', {
        filename: file.name,
        file_size: file.size,
        content_type: file.type,
        use_oss: true,
        oss_folder: 'documents',
        upload_mode: 'direct_oss',
        current_stage: 'preparing',
        stage_progress: {
          stage: 'preparing',
          progress: 0
        }
      });

      if (!taskResponse.data.success) {
        throw new Error(taskResponse.data.error || '创建上传任务失败');
      }

      task = taskResponse.data.data;
      console.log('上传任务创建成功:', task);

      // 立即显示任务列表，让用户看到上传进度
      setUploadTaskListVisible(true);

      const reportProgress = async (progress, uploadedBytes) => {
        const normalized = Math.min(progress, 99);
        if (normalized <= lastReportedProgress) {
          return;
        }
        lastReportedProgress = normalized;
        try {
          await axios.patch(`/api/upload-tasks/${task.id}/`, {
            uploaded_bytes: uploadedBytes,
            current_stage: 'uploading',
          });
        } catch (error) {
          console.warn('更新进度失败:', error);
        }
      };

      // 3. 前端直传OSS
      const OSS = (await import('ali-oss')).default;
      const ossConfig = {
        accessKeyId: stsData.access_key_id,
        accessKeySecret: stsData.access_key_secret,
        bucket: stsData.bucket,
        endpoint: stsData.endpoint,
        secure: true
      };
      
      // 只有在STS模式下才添加stsToken
      if (stsData.security_token && stsData.mode !== 'direct') {
        ossConfig.stsToken = stsData.security_token;
      }
      
      const ossClient = new OSS(ossConfig);

      // 更新任务状态为上传中
      await axios.patch(`/api/upload-tasks/${task.id}/`, {
        status: 'uploading',
        current_stage: 'uploading',
        started_at: new Date().toISOString()
      });

      // 开始上传（小文件用 put，大文件用分片）
      const uploadResult = file.size <= 5 * 1024 * 1024
        ? await ossClient.put(stsData.file_path, file, {
            progress: async (p) => {
              const progress = Math.round(p * 100);
              const uploadedBytes = Math.round(p * file.size);
              console.log('上传进度:', progress, '%');
              await reportProgress(progress, uploadedBytes);
            },
          })
        : await ossClient.multipartUpload(stsData.file_path, file, {
            progress: async (p) => {
              const progress = Math.round(p * 100);
              const uploadedBytes = Math.round(p * file.size);
              console.log('上传进度:', progress, '%');
              await reportProgress(progress, uploadedBytes);
            },
          });

      console.log('OSS上传完成:', uploadResult);

      // 4. 保存文件信息到后端
      const etag = uploadResult.etag || uploadResult.res?.headers?.etag;
      const fileInfoResponse = await axios.post('/api/oss/upload-callback/', {
        file_path: stsData.file_path,
        original_filename: file.name,
        file_size: file.size,
        content_type: file.type,
        etag,
      });

      if (!fileInfoResponse.data.success) {
        throw new Error(fileInfoResponse.data.error || '保存文件信息失败');
      }

      const ossFile = fileInfoResponse.data.data;
      console.log('文件信息保存成功:', ossFile);

      // 5. 创建文档记录
      const documentData = {
        title: values.title,
        group_id: values.group,
        folder_id: values.folder_id || selectedFolderId,
        oss_file_id: ossFile.id,
        use_oss: true
      };

      const documentResponse = await axios.post('/api/documents/', documentData);
      console.log('文档记录创建成功:', documentResponse.data);

      // 6. 更新任务状态为成功
      await axios.patch(`/api/upload-tasks/${task.id}/`, {
        status: 'success',
        progress: 100,
        uploaded_bytes: file.size,
        completed_at: new Date().toISOString(),
        current_stage: 'completed',
        related_document: documentResponse.data.id,
      });

      message.success(t('common.uploadSuccess'));

    } catch (error) {
      console.error('前端直传OSS失败:', error);
      message.error(`上传失败: ${error.message}`);
      
      // 如果有任务ID，更新任务状态为失败
      if (typeof task !== 'undefined' && task && task.id) {
        try {
          await axios.patch(`/api/upload-tasks/${task.id}/`, {
            status: 'failed',
            error_message: error.message,
            completed_at: new Date().toISOString()
          });
        } catch (updateError) {
          console.warn('更新任务状态失败:', updateError);
        }
      }
    } finally {
      setLoading(false);
      form.resetFields();
      setSelectedDocument(null);
      
      // 刷新文档列表
      await fetchDocuments(1, pagination.pageSize, filters, 'created_at', 'desc', selectedFolderId);
    }
  };

  // 使用文件URL上传（更新操作或已上传文件）
  const handleFileUrlUpload = async (fileUrl, values) => {
    try {
      console.log('=== 开始使用文件URL上传 ===');
      
      const formData = new FormData();
      formData.append('title', values.title);
      formData.append('use_oss', true);

      // 处理组别字段
      if (values.group) {
        formData.append('group_id', values.group);
      }

      // 处理文件夹ID
      if (values.folder_id) {
        formData.append('folder_id', values.folder_id);
      } else if (selectedFolderId) {
        formData.append('folder_id', selectedFolderId);
      }

      // 如果有文件URL，添加到FormData
      if (fileUrl) {
        formData.append('file_url', fileUrl);
      }

      const url = selectedDocument ? `/api/documents/${selectedDocument.id}/` : '/api/documents/';
      const method = selectedDocument ? 'PUT' : 'POST';

      const response = await axios({
        method: method,
        url: url,
        data: formData,
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (selectedDocument) {
        message.success(t('common.documentUpdateSuccess'));
      } else {
        message.success(t('common.uploadSuccess'));
      }

    } catch (error) {
      console.error('文件URL上传失败:', error);
      message.error(`操作失败: ${error.message}`);
    } finally {
      setLoading(false);
      form.resetFields();
      setSelectedDocument(null);
      
      // 刷新文档列表
      await fetchDocuments(1, pagination.pageSize, filters, 'created_at', 'desc', selectedFolderId);
    }
  };



  const handleCreateGroup = async (values) => {
    setLoading(true);
    try {
      await axios.post('/api/document-groups/', values);
              message.success(t('common.groupCreateSuccess'));
      setGroupModalVisible(false);
      groupForm.resetFields();
      fetchDocumentGroups(); // 刷新组别列表
    } catch (err) {
      // 优先展示后端返回的 name 字段错误
      const nameError = err.response?.data?.name;
      if (nameError) {
        message.error(nameError.join('，'));
      } else if (err.response?.data?.detail) {
        message.error(err.response.data.detail);
      } else {
        message.error('创建组别失败');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteDocument = async (record) => {
    try {
      await axios.delete(`/api/documents/${record.id}/`);
              message.success(t('common.documentDeleteSuccess'));
      fetchDocuments(pagination.current, pagination.pageSize, filters, 'created_at', 'desc', selectedFolderId);
    } catch (err) {
      console.error('删除文档失败:', err);
      message.error(err.response?.data?.detail || '删除文档失败');
    }
  };

  const handleDeleteFolder = async (folderId) => {
    // 找到要删除的文件夹信息
    const folderToDelete = currentLevelFolders.find(f => f.folderId === folderId);
    const folderName = folderToDelete?.name || '文件夹';
    
    // 权限检查：只有超级管理员和文件夹所有者才能删除
    if (!permissionManager.hasPermission('document', 'delete')) {
      message.error(t('common.noPermissionToDeleteFolder'));
      return;
    }
    
    if (currentUser && currentUser.role !== 'superadmin') {
      if (folderToDelete?.owner?.id !== currentUser?.id) {
        message.error(t('common.canOnlyDeleteOwnFolders'));
        return;
      }
    }
    
    // 显示确认对话框
    Modal.confirm({
      title: t('common.confirmDeleteFolder'),
      content: (
        <div>
          <p>{t('common.confirmDeleteFolderContent', { folderName })}</p>
          <p style={{ color: '#ff4d4f', fontSize: '14px' }}>
            ⚠️ {t('common.deleteFolderWarning')}
          </p>
        </div>
      ),
      okText: t('common.confirmDelete'),
      okType: 'danger',
      cancelText: t('common.cancel'),
      onOk: async () => {
        try {
          const response = await axios.delete(`/api/folders/${folderId}/`);
          
          // 显示删除结果
          if (response.data) {
            const { deleted_folders, deleted_documents, total_deleted } = response.data;
            message.success(
              `文件夹删除成功！共删除 ${deleted_folders} 个文件夹，${deleted_documents} 个文件`
            );
          } else {
            message.success(t('common.folderDeleteSuccess'));
          }
          
          // 刷新数据
          await fetchFolderTree(filters?.group);
          await fetchDocuments(pagination.current, pagination.pageSize, filters, 'created_at', 'desc', selectedFolderId);
        } catch (err) {
          console.error('删除文件夹失败:', err);
          const errorMsg = err.response?.data?.detail || '文件夹删除失败';
          message.error(errorMsg);
        }
      }
    });
  };

  const handleUpdateDocument = (record) => {
    console.log('=== 开始更新文档 ===');
    console.log('选中的文档记录:', record);
    console.log('文档组别信息:', record.group);
    console.log('组别ID:', record.group?.id);
    console.log('审核设置:', record.need_review);

    setSelectedDocument(record);
    setUploadModalVisible(true);
    setTitleTouched(false);
    
    // 预填充表单数据
    const formValues = {
      title: record.title,
      group: record.group?.id,
      need_review: record.need_review !== undefined ? record.need_review : false,
    };
    console.log('设置表单值:', formValues);
    form.setFieldsValue(formValues);
  };

  // 获取文件图标
  const getFileIcon = (fileName) => {
    const extension = fileName.split('.').pop().toLowerCase();
    return FILE_TYPE_ICONS[extension] || <FileOutlined />;
  };

  // 格式化文件大小
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // 统计信息
  const stats = useMemo(() => {
    const total = documents.length;

    const totalSize = documents.reduce((sum, d) => sum + (d.file_size || 0), 0);
          return { total, totalSize };
  }, [documents]);

  const columns = useMemo(() => {
    const baseColumns = [
      {
        title: t('common.document'),
        dataIndex: 'title',
        key: 'title',
        width: 300,
        render: (title, record) => (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {getFileIcon(record.file_url || title)}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div 
                style={{ 
                  fontWeight: 500, 
                  color: '#262626', 
                  marginBottom: 4,
                  cursor: 'pointer',
                  transition: 'color 0.2s'
                }}
                onMouseEnter={(e) => e.target.style.color = '#1890ff'}
                onMouseLeave={(e) => e.target.style.color = '#262626'}
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); handlePreview(record); }}
                title={t('common.clickToPreview')}
              >
                {title}
              </div>
              <div style={{ fontSize: 12, color: '#8c8c8c' }}>
                {record.file_url && formatFileSize(record.file_size || 0)}
              </div>
            </div>
          </div>
        ),
      },
    ];

    // 根据角色决定是否显示文件类型列
    if (!shouldHideFeature('file_type_column')) {
      baseColumns.push({
        title: t('common.fileType'),
        dataIndex: 'file_type_display',
        key: 'file_type_display',
        width: 120,
        render: (type) => type || '-',
      });
    }

    // 添加其他列
    baseColumns.push(
      {
        title: t('common.belongingSpace'),
        dataIndex: 'group',
        key: 'group',
        width: 120,
        render: (group) => (
          <div>
            {group ? (
              <Tag color="blue">{group.name}</Tag>
            ) : (
              <span style={{ color: '#8c8c8c', fontSize: 12 }}>{t('common.ungrouped')}</span>
            )}
          </div>
        ),
      },
      {
        title: t('common.owner'),
        dataIndex: 'owner',
        key: 'owner',
        width: 120,
        render: (owner) => (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Avatar size="small" style={{ backgroundColor: '#1890ff' }}>
              {owner?.username?.charAt(0)?.toUpperCase() || 'U'}
            </Avatar>
            <span style={{ fontSize: 12 }}>{owner?.username || t('common.unknown')}</span>
          </div>
        ),
      },
      {
        title: t('common.createdTime'),
        dataIndex: 'created_at',
        key: 'created_at',
        width: 160,
        sorter: true,
        defaultSortOrder: 'descend',
        render: (date) => (
          <div>
            <div>{dayjs(date).format('YYYY-MM-DD')}</div>
            <div style={{ fontSize: 11, color: '#8c8c8c' }}>
              {dayjs(date).format('HH:mm')}
            </div>
          </div>
        ),
      },
      {
        title: t('common.actions'),
        key: 'action',
        width: 120,
        fixed: 'right',
        render: (_, record) => {
          if (record.__isFolder) {
            // 检查文件夹删除权限：只有超级管理员和文件夹所有者才能删除
            const canDeleteFolder = (() => {
              if (!permissionManager.hasPermission('document', 'delete')) return false;
              if (currentUser && currentUser.role === 'superadmin') return true;
              return record.owner?.id === currentUser?.id;
            })();
            
            const folderMenu = (
              <Menu>
                <Menu.Item key="open" icon={<FolderOpenOutlined />} onClick={() => {
                  setSelectedFolderId(record.folderId);
                  setSelectedFolderName(record.name || '');
                  const next = { ...filters, folder: record.folderId };
                  setFilters(next);
                  fetchDocuments(1, pagination.pageSize || 10, next, 'created_at', 'desc', record.folderId);
                }}>
                  {t('common.open')}
                </Menu.Item>
                {canDeleteFolder ? (
                  <Menu.Item key="delete" icon={<DeleteOutlined />} danger onClick={() => handleDeleteFolder(record.folderId)}>
                    {t('common.delete')}
                  </Menu.Item>
                ) : (
                  <Menu.Item key="delete" icon={<DeleteOutlined />} disabled title={t('common.canOnlyDeleteOwnFolders')}>
                    {t('common.delete')}
                  </Menu.Item>
                )}
              </Menu>
            );
            
            return (
              <Dropdown overlay={folderMenu} trigger={['click']} placement="bottomRight">
                <Button type="text" icon={<EllipsisOutlined />} size="small" />
              </Dropdown>
            );
          }
          // 检查编辑权限：只有超级管理员和文件上传者才能编辑
          const canEdit = (() => {
            if (!permissionManager.hasPermission('document', 'edit')) return false;
            if (currentUser && currentUser.role === 'superadmin') return true;
            return record.owner?.id === currentUser?.id;
          })();
          
          // 检查删除权限：只有超级管理员和文件上传者才能删除
          const canDelete = (() => {
            if (!permissionManager.hasPermission('document', 'delete')) return false;
            if (currentUser && currentUser.role === 'superadmin') return true;
            return record.owner?.id === currentUser?.id;
          })();

          const menu = (
            <Menu>
              <Menu.Item key="preview" icon={<EyeOutlined />} onClick={() => handlePreview(record)}>
                {t('common.preview')}
              </Menu.Item>
              <Menu.Item key="detail" icon={<FileTextOutlined />} onClick={() => handleViewDetail(record)}>
                {t('common.details')}
              </Menu.Item>
              <Menu.Item key="download" icon={<DownloadOutlined />} onClick={() => downloadFile(record)}>
                {t('common.download')}
              </Menu.Item>
              <Menu.Item key="copy-link" icon={<CopyOutlined />} onClick={() => {
                if (!record.file_url) {
                  message.warning(t('common.documentNoFile'));
                  return;
                }
                const fullFileUrl = getMediaUrl(record.file_url);
                copy(fullFileUrl);
                message.success(t('common.fileLinkCopied'));
              }}>
                {t('common.copyLink')}
              </Menu.Item>

              {canEdit ? (
                <Menu.Item key="update" icon={<EditOutlined />} onClick={() => handleUpdateDocument(record)}>
                  {t('common.update')}
                </Menu.Item>
              ) : (
                <Menu.Item key="update" icon={<EditOutlined />} disabled title={t('common.canOnlyEditOwnDocuments')}>
                  {t('common.update')}
                </Menu.Item>
              )}
              {canDelete ? (
                <Menu.Divider />
              ) : (
                <Menu.Divider />
              )}
              {canDelete ? (
                <Menu.Item key="delete" icon={<DeleteOutlined />} danger onClick={() => handleDeleteDocument(record)}>
                  {t('common.delete')}
                </Menu.Item>
              ) : (
                <Menu.Item key="delete" icon={<DeleteOutlined />} disabled title={t('common.canOnlyDeleteOwnDocuments')}>
                  {t('common.delete')}
                </Menu.Item>
              )}
            </Menu>
          );

          return (
            <Dropdown overlay={menu} trigger={['click']} placement="bottomRight">
              <Button type="text" icon={<EllipsisOutlined />} size="small" />
            </Dropdown>
          );
        },
      }
    );

    return baseColumns;
  }, [handleViewDetail, handlePreview, downloadFile, handleDeleteDocument, handleUpdateDocument, filters, pagination.pageSize, shouldHideFeature]);

  // 表格行选择配置
  const rowSelection = {
    selectedRowKeys,
    onChange: (selectedKeys) => setSelectedRowKeys(selectedKeys),
    getCheckboxProps: (record) => ({ disabled: !!record.__isFolder }),
  };

  // 批量操作功能
  const handleBatchDelete = async () => {
    if (selectedRowKeys.length === 0) {
      message.warning('请选择要删除的文档');
      return;
    }

    // 过滤出用户有权限删除的文档（只有超级管理员和文件上传者才能删除）
    const deletableDocs = selectedRowKeys.filter(id => {
      const doc = documents.find(d => d.id === id);
      if (!doc) return false;
      
      // 超级管理员可以删除所有文档
      if (currentUser && currentUser.role === 'superadmin') {
        return permissionManager.hasPermission('document', 'delete');
      }
      
      // 普通用户只能删除自己上传的文档
      return doc.owner?.id === currentUser?.id && permissionManager.hasPermission('document', 'delete');
    });

    if (deletableDocs.length === 0) {
              message.warning(t('common.noPermissionToDelete'));
      return;
    }

    const nonDeletableCount = selectedRowKeys.length - deletableDocs.length;
    let messageText = t('common.confirmBatchDeleteContent', { count: deletableDocs.length });

    if (nonDeletableCount > 0) {
      messageText += `\n\n${t('common.batchDeletePermissionNote', { count: nonDeletableCount })}`;
    }

    messageText += `\n\n${t('common.operationIrreversible')}`;

    Modal.confirm({
      title: t('common.confirmBatchDelete'),
      content: messageText,
      okText: t('common.confirmDelete'),
      okType: 'danger',
      cancelText: t('common.cancel'),
      onOk: async () => {
        setLoading(true);
        try {
          await Promise.all(deletableDocs.map(id => axios.delete(`/api/documents/${id}/`)));
          message.success(`成功删除 ${deletableDocs.length} 个文档`);
          setSelectedRowKeys([]);
          fetchDocuments(pagination.current, pagination.pageSize, filters, 'created_at', 'desc', selectedFolderId);
        } catch (err) {
          message.error('批量删除失败');
        } finally {
          setLoading(false);
        }
      }
    });
  };

  // 批量下载功能
  const handleBatchDownload = () => {
    if (selectedRowKeys.length === 0) {
      message.warning('请选择要下载的文档');
      return;
    }

    const selectedDocs = documents.filter(doc => selectedRowKeys.includes(doc.id));
    const downloadableDocs = selectedDocs.filter(doc => doc.file_url);
    const nonDownloadableCount = selectedDocs.length - downloadableDocs.length;

    if (downloadableDocs.length === 0) {
      message.warning('您没有权限下载选中的文档或文档没有文件');
      return;
    }

    if (nonDownloadableCount > 0) {
      message.info(`开始下载 ${downloadableDocs.length} 个文档，其中 ${nonDownloadableCount} 个文档您没有下载权限`);
    } else {
      message.success(`开始下载 ${downloadableDocs.length} 个文档`);
    }

    // 使用统一的批量下载函数
    downloadFiles(downloadableDocs);
  };

  // 高级搜索功能
  const handleAdvancedSearch = async (values) => {
    const searchParams = {};

    if (values.keyword) searchParams.search = values.keyword;
    if (values.group) searchParams.group = values.group;
    if (values.status) searchParams.status = values.status;
    if (values.file_type) searchParams.file_type = values.file_type;
    if (values.date_range && values.date_range.length === 2) {
      searchParams.created_after = values.date_range[0].format('YYYY-MM-DD');
      searchParams.created_before = values.date_range[1].format('YYYY-MM-DD');
    }
    if (values.file_size_min) searchParams.file_size_min = values.file_size_min;
    if (values.file_size_max) searchParams.file_size_max = values.file_size_max;

    setFilters(searchParams);
    setAdvancedSearchVisible(false);
          fetchDocuments(1, pagination.pageSize, searchParams, 'created_at', 'desc', selectedFolderId);
  };

  // 拖拽上传功能
  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleOpenUploadModal();
      
      // 延迟设置文件，确保Upload组件已经渲染
      setTimeout(() => {
        // 获取Upload组件的ref，直接操作其内部状态
        const uploadRef = document.querySelector('.ant-upload input[type="file"]');
        if (uploadRef) {
          // 创建FileList对象
          const dataTransfer = new DataTransfer();
          dataTransfer.items.add(files[0]);
          
          // 设置input的files属性
          uploadRef.files = dataTransfer.files;
          
          // 触发change事件
          const event = new Event('change', { bubbles: true });
          uploadRef.dispatchEvent(event);
          
          // 设置标题
          setTimeout(() => {
            form.setFieldsValue({
              title: files[0].name
            });
          }, 100);
        }
      }, 200);
    }
  };

  // 图片压缩功能
  const compressImage = (file, maxWidth = 1920, quality = 0.8) => {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();

      img.onload = () => {
        // 计算压缩后的尺寸
        let { width, height } = img;
        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }

        canvas.width = width;
        canvas.height = height;

        // 绘制压缩后的图片
        ctx.drawImage(img, 0, 0, width, height);

        // 转换为Blob
        canvas.toBlob((blob) => {
          const compressedFile = new File([blob], file.name, {
            type: file.type,
            lastModified: Date.now(),
          });
          resolve(compressedFile);
        }, file.type, quality);
      };

      img.src = URL.createObjectURL(file);
    });
  };

  // 文件上传进度
  const uploadProps = {
    name: 'file',
    multiple: false,
    beforeUpload: async (file) => {
      const isLt10G = file.size / 1024 / 1024 < 10 * 1024; // 10GB = 10240MB
      if (!isLt10G) {
        message.error(t('common.fileTooLarge'));
        return false;
      }

      // 文件名长度校验
      if (file.name && file.name.length > 255) {
        message.error(t('common.fileNameTooLong'));
        return false;
      }

      // 检查文件类型
      const fileName = file.name;
      const extension = fileName.split('.').pop().toLowerCase();
      const supportedFormats = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'txt', 'md', 'markdown', ...SUPPORTED_IMAGE_FORMATS, ...SUPPORTED_VIDEO_FORMATS];

      if (!supportedFormats.includes(extension)) {
        message.error(`不支持的文件格式: ${extension}`);
        return false;
      }

      // 图片文件特殊处理
      if (isImageFile(fileName)) {
        // 检查图片尺寸
        if (file.size > 5 * 1024 * 1024) { // 5MB
          message.info('图片文件较大，正在压缩...');
          try {
            const compressedFile = await compressImage(file);
            // 替换原文件
            Object.assign(file, compressedFile);
            message.success('图片压缩完成');
          } catch (error) {
            console.error('图片压缩失败:', error);
            message.warning('图片压缩失败，将使用原文件');
          }
        }
      }

      return false; // 阻止自动上传，手动处理
    },
    onChange: (info) => {
      console.log('Upload onChange:', info);
      console.log('文件状态:', info.file.status);
      console.log('文件列表:', info.fileList);

      // 更新表单字段值
      form.setFieldsValue({ file: info.fileList });

      // 自动填充标题（仅在未手动输入时）
      if (!titleTouched && info.fileList && info.fileList.length > 0) {
        const fileName = info.fileList[0].name;
        form.setFieldsValue({ title: fileName });
      }

      // 强制触发表单验证
      setTimeout(() => {
        form.validateFields(['file']).then(() => {
          console.log('文件字段验证通过');
        }).catch((errors) => {
          console.log('文件字段验证失败:', errors);
        });
      }, 100);

      if (info.file.status === 'uploading') {
        setUploading(true);
        setUploadProgress(info.file.percent || 0);
      } else if (info.file.status === 'done') {
        setUploading(false);
        setUploadProgress(100);
        // 确保文件对象正确设置
        if (info.file.originFileObj) {
          console.log('文件对象设置成功:', info.file.name);
        }
      } else if (info.file.status === 'removed') {
        setUploading(false);
        setUploadProgress(0);
        console.log('文件已移除');
        // 清空表单字段
        form.setFieldsValue({ file: [] });
      } else if (info.file.status === 'error') {
        setUploading(false);
        setUploadProgress(0);
        console.log('文件上传错误:', info.file.error);
      }
    },
    showUploadList: {
      showPreviewIcon: true,
      showRemoveIcon: true,
      showDownloadIcon: false,
    },
    onPreview: (file) => {
      // 如果是图片文件，显示预览
      if (isImageFile(file.name)) {
        const url = file.url || URL.createObjectURL(file.originFileObj);
        const image = new Image();
        image.src = url;
        const imgWindow = window.open(url);
        imgWindow.document.write(image.outerHTML);
      }
    }
  };

  // 打开上传Modal时，默认选择用户所在的第一个空间
  const handleOpenUploadModal = () => {
    setSelectedDocument(null);
    setUploadModalVisible(true);
    setTitleTouched(false);
    
    // 优先选择用户所在的第一个空间，如果没有则选择可访问的第一个空间
    let defaultGroupId = undefined;
    if (currentUser && currentUser.user_spaces && currentUser.user_spaces.length > 0) {
      // 用户所在的第一个空间
      defaultGroupId = currentUser.user_spaces[0].id;
    } else if (documentGroups && documentGroups.length > 0) {
      // 可访问的第一个空间
      defaultGroupId = documentGroups[0].id;
    }
    
    // 设置默认空间/文件夹：
    if (selectedFolderId) {
      // 在文件夹内上传：空间固定为当前文件夹所在空间，文件夹固定为当前文件夹
      form.setFieldsValue({ 
        group: filters?.group || defaultGroupId, 
        folder_id: selectedFolderId,
        use_oss: true  // 默认使用OSS
      });
    } else {
      // 顶级上传：清空文件夹，空间按用户空间或第一个空间
      form.setFieldsValue({ 
        group: filters?.group || defaultGroupId, 
        folder_id: null,
        use_oss: true  // 默认使用OSS
      });
    }
  };

  const handleCreateFolder = async (vals) => {
    try {
      const payload = { name: vals.name };
      // 优先使用表单中选择的 group（顶级新建时必填）；否则退回当前 filters.group
      const formGroup = createFolderForm.getFieldValue('group');
      const groupId = formGroup || filters?.group || null;
      if (groupId) payload.group_id = groupId;
      if (currentUser?.id) payload.owner_id = currentUser.id;
      const res = await axios.post('/api/folders/', payload, { params: { group: groupId, current_parent: selectedFolderId } });
              message.success(t('common.folderCreateSuccess'));
      setCreateFolderModalVisible(false);
      createFolderForm.resetFields();

      // 本地乐观更新：将新文件夹加入到当前层级的 folderTree
      const newId = res?.data?.id;
      const newName = res?.data?.name;
      if (newId && newName) {
        const newNode = { id: newId, name: newName, children: [], group: groupId ? { id: groupId } : (filters?.group ? { id: filters.group } : null), owner: currentUser ? { id: currentUser.id, username: currentUser.username } : null };
        setFolderTree((prev) => {
          const insertInto = (nodes, parentId) => {
            if (!Array.isArray(nodes)) return nodes;
            if (!parentId) {
              // 根层级：将新节点插到最前
              return [newNode, ...nodes];
            }
            const next = nodes.map((n) => ({ ...n, children: Array.isArray(n.children) ? [...n.children] : [] }));
            const dfs = (arr) => {
              for (const n of arr) {
                if (n.id === parentId) {
                  n.children = [newNode, ...(n.children || [])];
                  return true;
                }
                if (n.children && n.children.length) {
                  const ok = dfs(n.children);
                  if (ok) return true;
                }
              }
              return false;
            };
            const found = dfs(next);
            // 若没找到父节点则退化为根插入
            return found ? next : [newNode, ...next];
          };
          return insertInto(prev || [], selectedFolderId);
        });
      }

      // 刷新文件夹树并留在当前列表
      fetchFolderTree(groupId);
      // 重新拉取当前列表数据
      fetchDocuments(1, pagination.pageSize || 10, filters, 'created_at', 'desc', selectedFolderId);
    } catch (e) {
      console.error('创建文件夹失败:', e);
      const detail = e?.response?.data ? (typeof e.response.data === 'string' ? e.response.data : JSON.stringify(e.response.data)) : '';
      message.error(`创建文件夹失败${detail ? ': ' + detail : ''}`);
    }
  };

  const buildTreeData = (nodes) => (nodes || []).map(function mapNode(n){
    return {
      title: n.name,
      key: n.id,
      children: buildTreeData(n.children || [])
    };
  });

  const handleFolderSelect = (keys, info) => {
    const fid = keys && keys.length ? keys[0] : null;
    setSelectedFolderId(fid);
    setSelectedFolderName(info?.node?.title || '');
    const next = { ...filters };
    if (fid) next.folder = fid; else next.folder = null;
    setFilters(next);
    fetchDocuments(1, pagination.pageSize || 10, next, 'created_at', 'desc', fid);
  };

  // 根据 folderId 在 folderTree 中查找节点
  const findFolderNode = useCallback((arr, id) => {
    if (!Array.isArray(arr)) return null;
    for (const n of arr) {
      if (n.id === id) return n;
      const sub = findFolderNode(n.children || [], id);
      if (sub) return sub;
    }
    return null;
  }, []);

  // 选中文件夹的空间ID
  const selectedFolderGroupId = useMemo(() => {
    if (!selectedFolderId) return null;
    const node = findFolderNode(folderTree || [], selectedFolderId);
    const g = node?.group;
    // group 可能是对象或ID
    if (g && typeof g === 'object') return g.id || null;
    if (typeof g === 'number') return g;
    return null;
  }, [folderTree, selectedFolderId, findFolderNode]);

  // 获取当前层级的子文件夹（用于列表前置显示）
  const currentLevelFolders = useMemo(() => {
    // 当选中文件夹时，显示其 children；否则显示当前空间的根 children
    const nodes = folderTree || [];
    const findNode = (arr, id) => {
      for (const n of arr) {
        if (n.id === id) return n;
        const sub = findNode(n.children || [], id);
        if (sub) return sub;
      }
      return null;
    };
    
    // 根据当前的过滤条件显示文件夹
    let displayNodes = nodes;
    if (!selectedFolderId) {
      if (filters?.group) {
        // 如果选择了特定空间，只显示该空间的文件夹
        displayNodes = nodes.filter(node => {
          const groupId = (node.group && typeof node.group === 'object') ? node.group.id : (typeof node.group === 'number' ? node.group : null);
          return groupId === filters.group;
        });
      } else {
        // 如果没有选择空间，显示用户有权限访问的所有空间的文件夹
        displayNodes = nodes.filter(node => {
          const groupId = (node.group && typeof node.group === 'object') ? node.group.id : (typeof node.group === 'number' ? node.group : null);
          return documentGroups.some(g => g.id === groupId);
        });
      }
    }
    
    const node = selectedFolderId ? findNode(nodes, selectedFolderId) : { children: displayNodes };
    return (node?.children || []).map(c => {
      const groupId = (c.group && typeof c.group === 'object') ? c.group.id : (typeof c.group === 'number' ? c.group : null);
      const groupObj = (() => {
        if (c.group && typeof c.group === 'object' && c.group.name) return c.group;
        const found = (documentGroups || []).find(g => g.id === groupId);
        return found ? { id: found.id, name: found.name } : (groupId ? { id: groupId, name: `#${groupId}` } : null);
      })();
      const ownerId = (typeof c.owner === 'object') ? (c.owner?.id ?? null) : (typeof c.owner === 'number' ? c.owner : null);
      const ownerName = (() => {
        if (typeof c.owner === 'object' && c.owner?.username) return c.owner.username;
        const u = (users || []).find(u => u.id === ownerId);
        return u ? (u.username || u.email || `用户#${ownerId}`) : (ownerId ? `用户#${ownerId}` : undefined);
      })();
      const ownerObj = ownerId ? { id: ownerId, username: ownerName } : (currentUser ? { id: currentUser.id, username: currentUser.username } : undefined);
      return {
        __isFolder: true,
        id: `folder-${c.id}`,
        name: c.name,
        folderId: c.id,
        groupId,
        group: groupObj,
        owner: ownerObj,
      };
    });
  }, [folderTree, selectedFolderId, documentGroups, users, currentUser, filters?.group]);

  const filteredDocumentsForView = useMemo(() => {
    const list = documents || [];
    if (selectedFolderId) {
      return list.filter(d => (d.folder && (d.folder.id === selectedFolderId)));
    }
    return list.filter(d => !d.folder);
  }, [documents, selectedFolderId]);

  const mergedRows = useMemo(() => {
    return [...currentLevelFolders, ...filteredDocumentsForView];
  }, [currentLevelFolders, filteredDocumentsForView]);

  const mergedColumns = useMemo(() => {
    // 在名称列渲染文件夹为可点击
    const nameColIdx = columns.findIndex(c => c.dataIndex === 'title' || c.key === 'title');
    if (nameColIdx >= 0) {
      const base = [...columns];
      const originRender = base[nameColIdx].render;
      base[nameColIdx] = {
        ...base[nameColIdx],
        render: (text, record) => {
          if (record.__isFolder) {
            return (
              <span style={{ cursor: 'pointer', color: '#1677ff' }} onClick={() => { 
                const fid = record.folderId; 
                setSelectedFolderId(fid); 
                setSelectedFolderName(record.name || ''); 
                const next = { ...filters }; 
                // 保持当前的过滤条件，不改变空间选择
                if (fid) next.folder = fid; else next.folder = null; 
                setFilters(next); 
                fetchDocuments(1, pagination.pageSize || 10, next, 'created_at', 'desc', fid); 
              }}>
                <FolderOutlined style={{ marginRight: 6 }} />{record.name}
              </span>
            );
          }
          return originRender ? originRender(text, record) : text;
        }
      };
      // 类型列：文件夹显示"文件夹"（仅当文件类型列存在时）
      const typeIdx = base.findIndex(c => c.dataIndex === 'file_type_display' || c.key === 'file_type_display');
      if (typeIdx >= 0) {
        const originTypeRender = base[typeIdx].render;
        base[typeIdx] = {
          ...base[typeIdx],
          render: (val, record) => record.__isFolder ? '文件夹' : (originTypeRender ? originTypeRender(val, record) : val)
        };
      }
      // 大小列：文件夹显示"-"
      const sizeIdx = base.findIndex(c => c.dataIndex === 'file_size' || c.key === 'file_size');
      if (sizeIdx >= 0) {
        const originSizeRender = base[sizeIdx].render;
        base[sizeIdx] = {
          ...base[sizeIdx],
          render: (val, record) => record.__isFolder ? '-' : (originSizeRender ? originSizeRender(val, record) : val)
        };
      }
      return base;
    }
    return columns;
  }, [columns, handleFolderSelect]);

  // 计算当前文件夹的面包屑路径
  const folderBreadcrumb = useMemo(() => {
    const nodes = folderTree || [];
    if (!selectedFolderId) return [];
    const path = [];
    const dfs = (arr, trail) => {
      for (const n of arr) {
        const nextTrail = [...trail, { id: n.id, name: n.name }];
        if (n.id === selectedFolderId) {
          path.splice(0, path.length, ...nextTrail);
          return true;
        }
        if (n.children && n.children.length) {
          if (dfs(n.children, nextTrail)) return true;
        }
      }
      return false;
    };
    dfs(nodes, []);
    return path;
  }, [folderTree, selectedFolderId]);

  if (viewMode === 'review') {
    return (
      <DocumentReviewPanel
        document={selectedDocument}
        onBack={() => {
          setSelectedDocument(null);
          setViewMode('list');
        }}
        onReviewSubmit={handleReviewFinish}
        loading={loading}
        reviewForm={reviewForm}
      />
    );
  }

  return (
    <div
      style={{
        background: '#fff',
        borderRadius: 10,
        boxShadow: '0 2px 8px #e5e5e5',
        padding: '16px 20px',
        flex: 1,
        minWidth: 0,
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {/* 统计信息卡片 */}
      <div style={{ 
        display: 'flex', 
        gap: 16, 
        marginBottom: 12, 
        flexShrink: 0,
        flexWrap: 'wrap',
        alignItems: 'center'
      }}>
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 8, 
          padding: '6px 12px',
          background: '#f8f9fa',
          borderRadius: 6,
          minWidth: 120
        }}>
          <FileOutlined style={{ color: '#1890ff', fontSize: 16 }} />
          <span style={{ fontSize: 14, color: '#666' }}>{t('common.totalDocuments')}：</span>
          <span style={{ fontSize: 16, fontWeight: 500, color: '#1890ff' }}>{stats.total}</span>
        </div>

        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 8, 
          padding: '6px 12px',
          background: '#f8f9fa',
          borderRadius: 6,
          minWidth: 120
        }}>
          <InboxOutlined style={{ color: '#722ed1', fontSize: 16 }} />
          <span style={{ fontSize: 14, color: '#666' }}>{t('common.totalSize')}：</span>
          <span style={{ fontSize: 16, fontWeight: 500, color: '#722ed1' }}>{formatFileSize(totalFileSize)}</span>
        </div>
      </div>

      {/* 工具栏 */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 12,
        flexWrap: 'wrap',
        gap: 8,
        flexShrink: 0
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          {/* 视图模式切换 */}
          {/* 视图切换按钮组 - 已隐藏
          <Radio.Group
            value={viewMode}
            onChange={(e) => setViewMode(e.target.value)}
            size="small"
          >
            <Radio.Button value="list">{t('common.listView')}</Radio.Button>
            <Radio.Button value="grid">{t('common.gridView')}</Radio.Button>
          </Radio.Group>
          */}

          {/* 筛选框 */}
          <Select
            placeholder={t('common.space')}
            value={filters?.group || undefined}
            onChange={(v) => handleFilterChange('group', v)}
            style={{ width: 120 }}
            allowClear
            showSearch
            optionFilterProp="children"
            size="small"
          >
            {filterGroups.map(group => (
              <Select.Option key={group.id} value={group.id}>
                <FolderOutlined style={{ marginRight: 8 }} />
                {group.name}
              </Select.Option>
            ))}
          </Select>

          {/* 所有者过滤框 - 根据角色控制显示 */}
          {!shouldHideFeature('owner_filter') && (
            <Select
              placeholder={t('common.owner')}
              value={filters?.owner || undefined}
              onChange={(v) => handleFilterChange('owner', v)}
              style={{ width: 120 }}
              allowClear
              showSearch
              optionFilterProp="children"
              size="small"
            >
              {filterUsers.map(user => (
                <Select.Option key={user.id} value={user.id}>
                  <UserOutlined style={{ marginRight: 8 }} />
                  {user.username}
                </Select.Option>
              ))}
            </Select>
          )}

          {/* 文件类型过滤框 - 根据角色控制显示 */}
          {!shouldHideFeature('file_type_filter') && (
            <Select
              placeholder={t('common.fileType')}
              value={filters?.file_type || undefined}
              onChange={(v) => handleFilterChange('file_type', v)}
              style={{ width: 120 }}
              allowClear
              size="small"
            >
              {fileTypeOptions.map((option, index) => (
                <Select.Option key={index} value={option.ext}>
                  {option.label}
                </Select.Option>
              ))}
            </Select>
          )}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          {/* 批量操作按钮 */}
          {selectedRowKeys.length > 0 && (
            <Space size="small">
              <Text type="secondary" style={{ fontSize: '12px' }}>{t('common.selectedItems', { count: selectedRowKeys.length })}</Text>
              {selectedRowKeys.some(id => {
                const doc = documents.find(d => d.id === id);
                return doc && permissionManager.hasPermission('document', 'view');
              }) && (
                <Button
                  size="small"
                  icon={<DownloadOutlined />}
                  onClick={handleBatchDownload}
                  style={{ background: theme.BUTTON_BG, color: '#231a17' }}
                  title={t('common.batchDownloadTooltip')}
                >
                  {t('common.batchDownload')}
                </Button>
              )}
              {selectedRowKeys.some(id => {
                const doc = documents.find(d => d.id === id);
                if (!doc) return false;
                
                // 超级管理员可以删除所有文档
                if (currentUser && currentUser.role === 'superadmin') {
                  return permissionManager.hasPermission('document', 'delete');
                }
                
                // 普通用户只能删除自己上传的文档
                return doc.owner?.id === currentUser?.id && permissionManager.hasPermission('document', 'delete');
              }) && (
                <Button
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={handleBatchDelete}
                  title={t('common.batchDeleteTooltip')}
                >
                  {t('common.batchDelete')}
                </Button>
              )}
            </Space>
          )}

          {/* 操作按钮 */}
          <Space>
            {/* 高级搜索按钮 - 根据角色控制显示 */}
            {!shouldHideFeature('advanced_search') && (
              <Button
                size="small"
                icon={<SearchOutlined />}
                onClick={() => setAdvancedSearchVisible(true)}
                style={{ background: theme.BUTTON_BG, color: '#231a17' }}
              >
                {t('common.advancedSearch')}
              </Button>
            )}
            <Button
              size="small"
              icon={<ReloadOutlined />}
              onClick={() => fetchDocuments(pagination.current, pagination.pageSize, filters, 'created_at', 'desc', selectedFolderId)}
              style={{ background: theme.BUTTON_BG, color: '#231a17' }}
            >
              {t('common.refresh')}
            </Button>
            {permissionManager.hasPermission('document', 'create') && (
              <Button
                type="primary"
                size="small"
                icon={<CloudUploadOutlined />}
                onClick={handleOpenUploadModal}
                style={{ background: theme.BUTTON_BG, color: '#231a17' }}
              >
                {t('common.uploadDocument')}
              </Button>
            )}

            <Button
              size="small"
              icon={<UploadOutlined />}
              onClick={() => setUploadTaskListVisible(true)}
              style={{ background: theme.BUTTON_BG, color: '#231a17' }}
            >
              {t('common.uploadTask')}
            </Button>

            <Button
              icon={<FolderOutlined />}
              onClick={() => setCreateFolderModalVisible(true)}
              style={{ background: theme.BUTTON_BG, color: '#231a17' }}
            >
              {t('common.createFolder')}
            </Button>
          </Space>
        </div>
      </div>

      {/* 面包屑：显示当前文件夹路径 */}
      {(folderBreadcrumb && folderBreadcrumb.length > 0) || filters?.group ? (
        <div style={{ marginTop: 8 }}>
          {folderBreadcrumb && folderBreadcrumb.length > 0 ? (
            <>
              <span
                style={{ color: '#1677ff', cursor: 'pointer' }}
                onClick={() => {
                  // 返回根目录：清空选中文件夹，但保持当前的过滤条件
                  setSelectedFolderId(null);
                  setSelectedFolderName('');
                  const next = { ...filters };
                  delete next.folder;
                  setFilters(next);
                  // 使用当前的过滤条件获取文档，包括空间过滤
                  fetchDocuments(1, pagination.pageSize || 10, next, 'created_at', 'desc', null);
                }}
              >
                {t('common.rootDirectory')}
              </span>
              {folderBreadcrumb.map((node, idx) => (
                <span key={node.id}>
                  <span style={{ margin: '0 8px', color: '#999' }}>/</span>
                  {idx === folderBreadcrumb.length - 1 ? (
                    <span style={{ fontWeight: 500 }}>{node.name}</span>
                  ) : (
                    <span
                      style={{ color: '#1677ff', cursor: 'pointer' }}
                      onClick={() => { 
                        const fid = node.id; 
                        setSelectedFolderId(fid); 
                        setSelectedFolderName(node.name || ''); 
                        const next = { ...filters }; 
                        // 保持当前的过滤条件，不改变空间选择
                        if (fid) next.folder = fid; else next.folder = null; 
                        setFilters(next); 
                        fetchDocuments(1, pagination.pageSize || 10, next, 'created_at', 'desc', fid); 
                      }}
                    >
                      {node.name}
                    </span>
                  )}
                </span>
              ))}
            </>
          ) : (
            // 仅显示根目录（虚拟文件夹）
            <span style={{ fontWeight: 500 }}>根目录</span>
          )}
        </div>
      ) : null}

      {/* 单列列表布局 */}
      <div style={{ marginTop: 12 }}>
        {/* 拖拽上传提示 */}
        {dragOver && (
          <div style={{ 
            border: '2px dashed #1890ff', 
            padding: '20px', 
            textAlign: 'center', 
            backgroundColor: '#f0faff', 
            borderRadius: '4px', 
            marginBottom: '12px'
          }}>
            <CloudUploadOutlined style={{ fontSize: 48, marginBottom: 16 }} />
            <div>{t('common.dragFilesHereToUpload')}</div>
          </div>
        )}

        {/* 文档列表容器 - 固定高度 */}
        <div style={{
          flex: 1,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0
        }}>
          {viewMode === 'list' ? (
            <div style={{ 
              display: 'flex', 
              flexDirection: 'column', 
              flex: 1,
              minHeight: 0,
              overflow: 'hidden'
            }}>
              <div style={{ 
                flex: 1, 
                overflow: 'auto',
                minHeight: 0,
                marginBottom: '16px'
              }}>
                <Table
                  columns={mergedColumns}
                  dataSource={mergedRows}
                  rowKey="id"
                  loading={loading}
                  pagination={false}
                  onChange={(pagination, filters, sorter) => handleTableSort(sorter)}
                  rowSelection={rowSelection}
                  scroll={{ x: 1200, y: 'calc(100vh - 300px)' }}
                  size="middle"
                  style={{ flex: 1 }}
                />
              </div>
              <div style={{ 
                padding: '16px 0',
                borderTop: '1px solid #f0f0f0',
                background: '#fff',
                flexShrink: 0,
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'center',
                minHeight: '60px'
              }}>
                <Pagination
                  current={pagination.current}
                  pageSize={pagination.pageSize}
                  total={pagination.total}
                  showSizeChanger={windowWidth > 768}
                  showQuickJumper={windowWidth > 768}
                  showTotal={(total, range) => t('common.paginationInfo', { start: range[0], end: range[1], total })}
                  pageSizeOptions={['10', '20', '50', '100']}
                  responsive={true}
                  size="default"
                  simple={windowWidth <= 768}
                  showLessItems={true}
                  onChange={(page, pageSize) => {
                    fetchDocuments(page, pageSize, filters, 'created_at', 'desc', selectedFolderId);
                  }}
                  onShowSizeChange={(current, size) => {
                    fetchDocuments(1, size, filters, 'created_at', 'desc', selectedFolderId);
                  }}
                />
              </div>
            </div>
          ) : (
            <>
              {/* 卡片视图的内容保留 */}
            </>
          )}
        </div>
      </div>

      {/* 预览 Modal：zIndex 需高于 Dashboard 顶栏(1100) */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingRight: 24 }}>
            <span>{t('common.onlinePreview')}</span>
            <Button
              type="text"
              size="small"
              icon={fullscreenPreview ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
              onClick={() => setFullscreenPreview(!fullscreenPreview)}
            />
          </div>
        }
        open={previewModalVisible}
        onCancel={() => {
          setPreviewModalVisible(false);
          setFullscreenPreview(false);
        }}
        footer={null}
        width={fullscreenPreview ? '100vw' : '80%'}
        centered={!fullscreenPreview}
        zIndex={1200}
        getContainer={() => document.body}
        destroyOnClose
        styles={{
          body: {
            maxHeight: fullscreenPreview ? 'calc(100vh - 110px)' : '75vh',
            overflow: 'auto',
          },
        }}
        style={fullscreenPreview ? { top: 0, paddingBottom: 0, maxWidth: '100vw' } : undefined}
      >
        {selectedDocument && <DocumentPreview document={selectedDocument} />}
      </Modal>

      {/* 详情 Modal */}
      <Modal
        title={t('common.documentDetails')}
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={
          <Button onClick={() => setDetailModalVisible(false)} style={{ background: theme.BUTTON_BG, color: '#231a17' }}>
                        {t('common.close')}
            </Button>
        }
      >
        {selectedDocument && (
          <Card>
            <Row gutter={[16, 16]}>
              <Col span={24}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                  <div style={{ fontSize: 48, color: '#1890ff' }}>
                    {getFileIcon(selectedDocument.file_url || selectedDocument.title)}
                  </div>
                  <div>
                    <Title level={4} style={{ margin: 0 }}>{selectedDocument.title}</Title>
                    <Text type="secondary">{formatFileSize(selectedDocument.file_size || 0)}</Text>
                  </div>
                </div>
              </Col>

              <Col span={12}>
                <div style={{ marginBottom: 8 }}>
                  <Text strong>{t('common.belongingSpace')}:</Text>
                  {selectedDocument.group ? (
                    <Tag color="blue" icon={<FolderOutlined />} style={{ marginLeft: 8 }}>
                      {selectedDocument.group.name}
                    </Tag>
                  ) : (
                    <Tag color="default" style={{ marginLeft: 8 }}>{t('common.ungrouped')}</Tag>
                  )}
                </div>
              </Col>

              <Col span={12}>
                <div style={{ marginBottom: 8 }}>
                  <Text strong>{t('common.owner')}:</Text>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
                    <Avatar size="small" style={{ backgroundColor: '#1890ff' }}>
                      {selectedDocument.owner?.username?.charAt(0)?.toUpperCase() || 'U'}
                    </Avatar>
                    <span>{selectedDocument.owner?.username || t('common.unknown')}</span>
                  </div>
                </div>
              </Col>



              <Col span={12}>
                <div style={{ marginBottom: 8 }}>
                  <Text strong>{t('common.createdTime')}:</Text>
                  <div style={{ marginTop: 4 }}>
                    {dayjs(selectedDocument.created_at).format('YYYY-MM-DD HH:mm:ss')}
                  </div>
                </div>
              </Col>

              <Col span={12}>
                <div style={{ marginBottom: 8 }}>
                  <Text strong>{t('common.updatedTime')}:</Text>
                  <div style={{ marginTop: 4 }}>
                    {dayjs(selectedDocument.updated_at).format('YYYY-MM-DD HH:mm:ss')}
                  </div>
                </div>
              </Col>


            </Row>
          </Card>
        )}
      </Modal>

      {/* 高级搜索抽屉 */}
      <Drawer
        title={t('common.advancedSearch')}
        placement="right"
        width={400}
        open={advancedSearchVisible}
        onClose={() => setAdvancedSearchVisible(false)}
        footer={
          <div style={{ textAlign: 'right' }}>
            <Button onClick={() => setAdvancedSearchVisible(false)} style={{ marginRight: 8 }}>
              {t('common.cancel')}
            </Button>
            <Button
              type="primary"
              onClick={() => searchForm.submit()}
              style={{ background: theme.BUTTON_BG, color: '#231a17' }}
            >
              {t('common.search')}
            </Button>
          </div>
        }
      >
        <Form form={searchForm} onFinish={handleAdvancedSearch} layout="vertical">
          <Form.Item name="keyword" label={t('common.keywordSearch')}>
            <Input placeholder={t('common.searchDocumentTitleDescription')} prefix={<SearchOutlined />} />
          </Form.Item>

          <Form.Item name="group" label={t('common.belongingSpace')}>
            <Select placeholder={t('common.selectSpace')} allowClear showSearch optionFilterProp="children">
              {documentGroups.map(group => (
                <Select.Option key={group.id} value={group.id}>
                  <FolderOutlined style={{ marginRight: 8 }} />
                  {group.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>



          {/* 文件类型选项 - 根据角色控制显示 */}
          {!shouldHideFeature('file_type_filter') && (
            <Form.Item name="file_type" label={t('common.fileType')}>
              <Select placeholder={t('common.selectFileType')} allowClear>
                {fileTypeOptions.map((option, index) => (
                  <Select.Option key={index} value={option.ext}>
                    {option.label}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          )}

          <Form.Item name="date_range" label={t('common.createdTime')}>
            <RangePicker
              style={{ width: '100%' }}
              placeholder={[t('common.startDate'), t('common.endDate')]}
            />
          </Form.Item>

          <Form.Item label={t('common.fileSizeRange')}>
            <Input.Group compact>
              <Form.Item name="file_size_min" noStyle>
                <InputNumber
                  placeholder={t('common.minimum')}
                  style={{ width: '50%' }}
                  min={0}
                  addonAfter="MB"
                />
              </Form.Item>
              <Form.Item name="file_size_max" noStyle>
                <InputNumber
                  placeholder={t('common.maximum')}
                  style={{ width: '50%' }}
                  min={0}
                  addonAfter="MB"
                />
              </Form.Item>
            </Input.Group>
          </Form.Item>
        </Form>
      </Drawer>

      {/* 上传 Modal */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <CloudUploadOutlined />
            {selectedDocument ? t('common.updateDocument') : t('common.uploadNewDocument')}
          </div>
        }
        open={uploadModalVisible}
        onCancel={() => {
          setUploadModalVisible(false);
          setSelectedDocument(null);
          form.resetFields();
          setUploadProgress(0);
        }}
        footer={(
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Button onClick={() => setUploadModalVisible(false)}>{t('common.cancel')}</Button>
            <Button type="primary" loading={loading} icon={<CloudUploadOutlined />} onClick={() => form.submit()}>
              {selectedDocument ? t('common.updateDocument') : t('common.uploadDocument')}
            </Button>
          </div>
        )}
        destroyOnHidden
        width={600}
        styles={{ body: { maxHeight: '70vh', overflow: 'auto' } }}
      >
        {uploading && (
          <div style={{ marginBottom: 16 }}>
            <Progress percent={uploadProgress} status="active" />
            <Text type="secondary">{t('common.uploadingFile')}</Text>
          </div>
        )}

        <Form form={form} onFinish={handleUploadFinish} layout="vertical">
          <Form.Item name="title" label={t('common.documentTitle')} rules={[{ required: true, message: t('common.pleaseEnterDocumentTitle') }]}> 
            <Input placeholder={t('common.pleaseEnterDocumentTitle')} onChange={() => setTitleTouched(true)} />
          </Form.Item>
          
          {/* 隐藏OSS开关，默认使用OSS存储
          <Form.Item name="use_oss" label="存储方式" valuePropName="checked" initialValue={true}>
            <Switch 
              checkedChildren="OSS云存储" 
              unCheckedChildren="本地存储"
            />
          </Form.Item>
          */}
          
          <Form.Item name="group" label={t('common.belongingSpace')} rules={[{ required: !selectedFolderId, message: t('common.pleaseSelectBelongingSpace') }]}> 
            <Select
              placeholder={selectedFolderId ? t('common.lockedToCurrentFolderSpace') : t('common.selectSpaceRequired')}
              allowClear={!selectedFolderId}
              disabled={!!selectedFolderId}
              showSearch
              optionFilterProp="children"
              onChange={(value) => {
                if (!selectedFolderId) {
                  form.setFieldsValue({ group: value });
                }
              }}
            >
              {documentGroups.map(group => (
                <Select.Option key={group.id} value={group.id}>
                  <FolderOutlined style={{ marginRight: 8 }} />
                  {group.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          {/* 隐藏所属文件夹选项
          <Form.Item name="folder_id" label={t('common.belongingFolder')}> 
            <TreeSelect
              style={{ width: '100%' }}
              value={selectedFolderId || null}
              dropdownStyle={{ maxHeight: 400, overflow: 'auto' }}
              placeholder={selectedFolderId ? t('common.lockedToCurrentFolder') : t('common.selectFolderOptional')}
              allowClear={!selectedFolderId}
              disabled={!!selectedFolderId}
              treeDefaultExpandAll
              treeData={(folderTree || []).map(function mapNode(n){
                return {
                  title: n.name,
                  value: n.id,
                  key: n.id,
                  children: (n.children || []).map(mapNode)
                };
              })}
              onChange={(v) => {
                if (!selectedFolderId) setSelectedFolderId(v || null);
                form.setFieldsValue({ folder_id: v || null });
              }}
            />
          </Form.Item>
          */}

          {/* 当前文件夹只读显示，虚拟根目录也显示 */}
          <Form.Item label={t('common.currentFolder')}>
            <Input value={selectedFolderId ? (selectedFolderName || `#${selectedFolderId}`) : t('common.rootDirectory')} disabled />
          </Form.Item>

          <Form.Item
            name="file"
            label={t('common.file')}
            valuePropName="fileList"
            getValueFromEvent={(e) => Array.isArray(e) ? e : e?.fileList}
            rules={[
              {
                validator: (_, value) => {
                  if (selectedDocument) {
                    return Promise.resolve();
                  } else {
                    if (!value || value.length === 0) {
                      return Promise.reject(new Error(t('common.pleaseSelectFile')));
                    }
                    return Promise.resolve();
                  }
                }
              }
            ]}
          >
            <Upload
              {...uploadProps}
              listType="picture-card"
              maxCount={1}
              accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.md,.markdown,.png,.jpg,.jpeg,.gif,.bmp,.webp,.svg,.tiff,.tif,.mp4,.avi,.mov,.wmv,.flv,.webm"
            >
              <div style={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center', 
                justifyContent: 'center',
                height: '100%',
                padding: '12px'
              }}>
                <CloudUploadOutlined style={{ fontSize: 20, color: '#999', marginBottom: 6 }} />
                <div style={{ fontSize: 12, color: '#666', textAlign: 'center' }}>
                  {t('common.clickOrDragToUpload')}
                </div>
              </div>
            </Upload>
            <div style={{ fontSize: 12, color: '#666', marginTop: 8 }}>
              <div>📄 {t('common.documentFormats')} (PDF, DOC, DOCX, TXT, MD)</div>
              <div>📊 {t('common.spreadsheetFormats')}</div>
              <div>🖼️ {t('common.imageFormats')}</div>
              <div>🎬 {t('common.videoFormats')}</div>
              <div>💡 {t('common.imageAutoCompress')}</div>
              <div>☁️ {t('common.defaultUploadToOSS')}</div>
              <div>📏 {t('common.largeFileQueueUpload')}</div>
            </div>
          </Form.Item>

          {selectedDocument && (
            <Alert
              message={t('common.currentFileInfo')}
              description={
                <div>
                  <div>{t('common.fileName')}: {selectedDocument.file_url ? selectedDocument.file_url.split('/').pop() : t('common.noFile')}</div>
                  <div>{t('common.fileSize')}: {formatFileSize(selectedDocument.file_size || 0)}</div>
                  <div style={{ color: '#999', fontSize: 12 }}>
                    {t('common.keepCurrentFile')}
                  </div>
                </div>
              }
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
            />
          )}
        </Form>
      </Modal>

      {/* 新建空间 Modal */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <FolderOutlined />
            {t('common.createSpace')}
          </div>
        }
        open={groupModalVisible}
        onCancel={() => setGroupModalVisible(false)}
        footer={null}
        destroyOnHidden
      >
        <Form form={groupForm} onFinish={handleCreateGroup} layout="vertical">
          <Form.Item name="name" label={t('common.spaceName')} rules={[{ required: true, message: t('common.pleaseEnterSpaceName') }]}>
            <Input placeholder={t('common.pleaseEnterSpaceName')} />
          </Form.Item>
          <Form.Item name="description" label={t('common.spaceDescription')}>
            <Input.TextArea placeholder={t('common.pleaseEnterSpaceDescription')} rows={4} />
          </Form.Item>
          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              icon={<FolderOutlined />}
              style={{ background: theme.BUTTON_BG, color: '#231a17' }}
              block
            >
              {t('common.createSpace')}
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      {/* 新建文件夹 Modal */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <FolderOutlined />
            {t('common.createFolder')}
          </div>
        }
        open={createFolderModalVisible}
        onCancel={() => setCreateFolderModalVisible(false)}
        footer={null}
        destroyOnHidden
      >
        <Form form={createFolderForm} onFinish={handleCreateFolder} layout="vertical">
          <Form.Item name="name" label={t('common.folderName')} rules={[{ required: true, message: t('common.pleaseEnterFolderName') }]}>
            <Input placeholder={t('common.enterFolderName')} />
          </Form.Item>

          {/* 顶级需要选择空间；在文件夹下时锁定空间 */}
          <Form.Item name="group" label={t('common.belongingSpace')} rules={[{ required: !selectedFolderId, message: t('common.pleaseSelectBelongingSpace') }]}>
            <Select
              placeholder={selectedFolderId ? t('common.lockedToCurrentFolderSpace') : t('common.selectSpaceRequired')}
              allowClear={!selectedFolderId}
              disabled={!!selectedFolderId}
              value={filters?.group || undefined}
              onChange={(v) => {
                if (!selectedFolderId) {
                  createFolderForm.setFieldsValue({ group: v });
                }
              }}
            >
              {(documentGroups || []).map(group => (
                <Select.Option key={group.id} value={group.id}>{group.name}</Select.Option>
              ))}
            </Select>
          </Form.Item>

          {/* 在文件夹下新建时锁定父文件夹 */}
          <Form.Item label={t('common.parentFolder')}>
            <Input value={selectedFolderName || (selectedFolderId ? `#${selectedFolderId}` : t('common.none'))} disabled />
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit" icon={<FolderOutlined />}>{t('common.create')}</Button>
          </Form.Item>
        </Form>
      </Modal>

      {/* 上传任务列表 - 右侧边栏 */}
      <UploadTaskList
        visible={uploadTaskListVisible}
        onClose={() => setUploadTaskListVisible(false)}
        onTaskComplete={(completedTasks) => {
          // 任务完成时刷新文档列表
          fetchDocuments(1, pagination.pageSize, filters, 'created_at', 'desc', selectedFolderId);
        }}
      />
    </div>
  );
};

export default DocumentManage;
